import { motion, useInView } from "framer-motion";
import { useRef } from "react";

const stats = [
  { number: "33", label: "Years at PwC" },
  { number: "370K", label: "Professionals Led" },
  { number: "$1B+", label: "GenAI Investment Led" },
  { number: "#1", label: "ChatGPT Enterprise Customer" },
];

export default function SolutionSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section data-testid="solution-section" ref={ref} id="about" className="py-24 md:py-36 bg-[#1A1E2B] relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-r from-[#1A1E2B] via-transparent to-[#1A1E2B]/80 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center">
          <motion.div
            className="md:col-span-7 order-2 md:order-1"
            initial={{ opacity: 0, y: 40 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <p className="text-xs uppercase tracking-[0.3em] text-[#C4963B] font-semibold mb-6">
              The Advantage
            </p>
            <h2 className="font-['Cormorant_Garamond'] text-3xl md:text-5xl leading-[1.1] mb-8 text-[#F9F6F0]">
              A leader who's already walked the path.
            </h2>
            <div className="space-y-5 text-[#E8E6E1]/55 text-base md:text-lg leading-relaxed">
              <p>
                Joe Atkinson isn't a futurist with a TED talk. He's a CPA who was elected by his
                partners to lead PwC's technology and products division. He didn't just write about
                AI transformation—he designed the digital foundation for it a decade ago as US
                Chief Digital Officer.
              </p>
              <p>
                Today, as Global Chief AI Officer, he's leading 370,000 people into the
                Intelligence Age—equipping them with skills, tools, and a Responsible AI
                charter. He architected PwC's landmark ChatGPT Enterprise agreement with
                OpenAI, making PwC the world's largest primary customer. He brings you the
                unvarnished truth of what it actually takes.
              </p>
              <p className="text-[#C4963B]/90 font-medium italic font-['Cormorant_Garamond'] text-xl">
                "Human-led, tech-powered. That's the only way transformation sticks."
              </p>
            </div>

            <div className="flex flex-wrap gap-8 mt-10 pt-10 border-t border-white/[0.08]">
              {stats.map((s) => (
                <div key={s.label}>
                  <p className="font-['Cormorant_Garamond'] text-4xl md:text-5xl text-[#C4963B] font-light">
                    {s.number}
                  </p>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#E8E6E1]/35 mt-2 font-medium">
                    {s.label}
                  </p>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            className="md:col-span-5 order-1 md:order-2"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="relative">
              <div className="absolute -inset-6 bg-[#C4963B]/[0.04] blur-3xl rounded-full" />
              <img
                src="/joe-profile-2.jpg"
                alt="Joe Atkinson"
                className="relative w-full h-[420px] md:h-[540px] object-cover object-top rounded-sm"
                loading="lazy"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#1A1E2B] to-transparent h-32" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
